import { FC } from 'react';
import { GameCardContainerType } from '../../GameCard/GameCard.types';
import { Grid, GridItem } from '../../../shared/Grid';
import { useBreakpointInfo, MediaBreakpoint } from '../../../../hooks/useDeviceInfo';
import { renderGameCard, BaseLayoutProps } from '../utils';
import {
  TALL_WIDE_LAYOUT_GAMES_COUNT,
  DOUBLE_LAYOUT_GAMES_COUNT,
} from '../../../../utils/games/createTileRowCollections';

const WIDE_LAYOUT_HERO_SKIP_COUNT = 1;

type WideLayoutProps = BaseLayoutProps;

export const WideLayout: FC<WideLayoutProps> = ({ games, getCompositeKey, ...props }) => {
  if (games.length < TALL_WIDE_LAYOUT_GAMES_COUNT) return null;
  const [firstGame] = games;

  return (
    <Grid columns={2} gap="s" className="game-grid game-grid--wide">
      <GridItem startCol={1} endCol={3}>
        {renderGameCard(firstGame, GameCardContainerType.Wide, 'wide-hero', props)}
      </GridItem>
      {games.slice(WIDE_LAYOUT_HERO_SKIP_COUNT).map((game, index) => (
        <GridItem key={getCompositeKey(game, `wide-${index + 1}`)}>
          {renderGameCard(game, GameCardContainerType.Box, `wide-${index + 1}`, props)}
        </GridItem>
      ))}
    </Grid>
  );
};

export const WideReverseLayout: FC<WideLayoutProps> = ({ games, getCompositeKey, ...props }) => {
  if (games.length < TALL_WIDE_LAYOUT_GAMES_COUNT) return null;
  const [firstGame, secondGame, thirdGame] = games;

  return (
    <Grid columns={2} gap="s" className="game-grid game-grid--wide-reverse">
      {[firstGame, secondGame].map((game, index) => (
        <GridItem key={getCompositeKey(game, `wide-reverse-${index}`)}>
          {renderGameCard(game, GameCardContainerType.Box, `wide-reverse-${index}`, props)}
        </GridItem>
      ))}
      <GridItem startCol={1} endCol={3}>
        {renderGameCard(thirdGame, GameCardContainerType.Wide, 'wide-reverse-hero', props)}
      </GridItem>
    </Grid>
  );
};

export const DoubleTallLayout: FC<WideLayoutProps> = ({ games, getCompositeKey, ...props }) => {
  const isMobileDevice = useBreakpointInfo([MediaBreakpoint.BELOW_MID_DESKTOP]);

  if (games.length < DOUBLE_LAYOUT_GAMES_COUNT) return null;

  if (isMobileDevice) {
    return (
      <Grid columns={2} gap="s" className="game-grid game-grid--double-tall-mobile">
        {games.map((game, index) => (
          <GridItem key={getCompositeKey(game, `double-tall-mobile-${index}`)} startCol={1} endCol={3}>
            {renderGameCard(game, GameCardContainerType.Wide, `double-tall-mobile-${index}`, props)}
          </GridItem>
        ))}
      </Grid>
    );
  }

  return (
    <Grid columns={2} gap="s" className="game-grid game-grid--double-tall">
      {games.map((game, index) => (
        <GridItem key={getCompositeKey(game, `double-tall-${index}`)}>
          {renderGameCard(game, GameCardContainerType.Tall, `double-tall-${index}`, props)}
        </GridItem>
      ))}
    </Grid>
  );
};

export const DoubleWideLayout: FC<WideLayoutProps> = ({ games, getCompositeKey, ...props }) => {
  const isMobileDevice = useBreakpointInfo([MediaBreakpoint.BELOW_MID_DESKTOP]);

  if (games.length < DOUBLE_LAYOUT_GAMES_COUNT) return null;

  const className = isMobileDevice ? 'game-grid game-grid--double-wide-mobile' : 'game-grid game-grid--double-wide';

  return (
    <Grid columns={2} gap="s" className={className}>
      {games.map((game, index) => (
        <GridItem key={getCompositeKey(game, `double-wide-${index}`)} startCol={1} endCol={3}>
          {renderGameCard(game, GameCardContainerType.Wide, `double-wide-${index}`, props)}
        </GridItem>
      ))}
    </Grid>
  );
};
